import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Gift, RefreshCw } from 'lucide-react';
import './customerGiftHours.css';
import { fetchUsoHorasRegaloPorCliente, UsoHorasRegalo } from '../../../services/usarHorasDeRegaloService.ts';

interface Props { customerUserId: number; sucursalId: number; }

const localDateKey = () => {
  const now=new Date();
  return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
};
const minutesFor = (use: UsoHorasRegalo) => Math.max(0,Math.floor(Number(use.minutos_disponibles ?? ((use.horas_regalo_aplicadas-(use.horas_usadas||0))*60))||0));
const formatMinutes = (minutes:number) => {
  const hours=Math.floor(minutes/60), rest=minutes%60;
  if(hours&&rest)return `${hours} h ${rest} min`;
  if(hours)return `${hours} h`;
  return `${rest} min`;
};

const CustomerGiftHoursPanel:React.FC<Props>=({customerUserId})=>{
  const [uses,setUses]=useState<UsoHorasRegalo[]>([]);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState('');
  const requestRef=useRef(0);

  const load=useCallback(async()=>{
    const requestId=++requestRef.current;
    setLoading(true);setError('');
    try{
      const nextUses=await fetchUsoHorasRegaloPorCliente(customerUserId);
      if(requestId===requestRef.current)setUses(nextUses);
    }
    catch{if(requestId===requestRef.current){setUses([]);setError('No se pudo consultar el saldo de horas de regalo.');}}
    finally{if(requestId===requestRef.current)setLoading(false);}
  },[customerUserId]);
  useEffect(()=>{
    setUses([]);
    void load();
    return()=>{requestRef.current+=1;};
  },[load]);
  const eligible=useMemo(()=>{
    const today=localDateKey();
    return uses.filter(use=>use.fecha_inicio_periodo<=today&&use.fecha_fin_periodo>=today&&minutesFor(use)>0);
  },[uses]);
  const total=eligible.reduce((sum,use)=>sum+minutesFor(use),0);
  const displayDate = (value:string) => new Intl.DateTimeFormat('es-BO', {
    day:'2-digit',month:'short',year:'numeric',
  }).format(new Date(`${value}T12:00:00`));

  return <section aria-label="Horas de regalo del cliente" className="customer-gift-balance">
    <div className="customer-gift-balance__heading">
      <span className="customer-gift-balance__icon"><Gift size={20} aria-hidden="true"/></span>
      <div><h3>Horas de regalo</h3><p>Saldo restante del cliente</p></div>
      <button type="button" aria-label="Actualizar saldo de horas de regalo" disabled={loading} onClick={()=>void load()} className="customer-gift-balance__refresh"><RefreshCw size={15} aria-hidden="true"/></button>
    </div>
    {loading ? <p role="status" className="customer-gift-balance__message">Consultando saldo…</p> : error ? <p role="alert" className="customer-gift-balance__error">{error}</p> : <>
      <div className="customer-gift-balance__total"><strong>{formatMinutes(total)}</strong><span>disponibles</span></div>
      {total>0 ? <>
        <p className="customer-gift-balance__count">{eligible.length} beneficio{eligible.length===1?'':'s'} vigente{eligible.length===1?'':'s'}</p>
        <ul className="customer-gift-balance__benefits">
          {eligible.map(use=><li key={use.id}>
            <div><span>{use.config_horas_regalo_display||'Horas de regalo'}</span><small>Vence el {displayDate(use.fecha_fin_periodo)}</small></div>
            <strong>{formatMinutes(minutesFor(use))}</strong>
          </li>)}
        </ul>
      </> : <p className="customer-gift-balance__message">Este cliente no tiene horas de regalo disponibles.</p>}
    </>}
  </section>;
};
export default CustomerGiftHoursPanel;
